// =============================================================================
// pages/DashboardModern.js — Real-time Dashboard (Faz F)
// =============================================================================
// Mik-San yönetim merkezi: bugün ciro, ay karşılaştırma, aktif sipariş, stok
// alert, NPS skor, segment dağılımı, top ürünler, 7 gün ciro grafiği.
// Her 30 saniyede bir refresh.
// =============================================================================

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Grid, Card, CardContent, Stack, Chip, IconButton,
  LinearProgress, Avatar, Tooltip, Alert
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StarIcon from "@mui/icons-material/Star";
import GroupsIcon from "@mui/icons-material/Groups";
import EmailIcon from "@mui/icons-material/Email";
import { getDashboardKPI, getAdminNotifications } from "../services/api";

function formatTL(amount) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 })
    .format(parseFloat(amount) || 0);
}

function StatCard({ icon, color, label, value, sub, trend }) {
  return (
    <Card sx={{
      borderTop: `4px solid ${color}`,
      transition: 'all 0.2s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }
    }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
              {label}
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ color, my: 0.5 }}>
              {value}
            </Typography>
            {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
            {trend != null && (
              <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
                {trend >= 0
                  ? <TrendingUpIcon sx={{ color: '#10b981', fontSize: 16 }} />
                  : <TrendingDownIcon sx={{ color: '#ef4444', fontSize: 16 }} />}
                <Typography variant="caption" sx={{ color: trend >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                  {trend >= 0 ? '+' : ''}{trend}% (geçen ay)
                </Typography>
              </Stack>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color + '20', color }}>
            {icon}
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );
}

function MiniBarChart({ data, height = 80 }) {
  if (!data || data.length === 0) return <Box sx={{ height }} />;
  const max = Math.max(...data.map(d => parseFloat(d.revenue) || 0));
  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ height }}>
      {data.map((d, i) => {
        const h = max > 0 ? (parseFloat(d.revenue) / max) * 100 : 0;
        return (
          <Tooltip key={i} title={`${d.d}: ${formatTL(d.revenue)} (${d.orders} sipariş)`}>
            <Box flex={1} sx={{
              height: `${h}%`,
              minHeight: 4,
              bgcolor: '#1565C0',
              borderRadius: '4px 4px 0 0',
              opacity: 0.7,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 }
            }} />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

export default function DashboardModern() {
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const [k, n] = await Promise.all([
        getDashboardKPI(),
        getAdminNotifications().catch(() => ({ data: { items: [] } }))
      ]);
      setKpi(k.data);
      setNotifications(n.data.items || []);
      setLastUpdate(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);  // 30 saniyede bir refresh
    return () => clearInterval(interval);
  }, [load]);

  if (loading || !kpi) return <Box p={3}><LinearProgress /></Box>;

  // Trend hesabı (geçen ay'a göre)
  const monthRev = parseFloat(kpi.this_month?.revenue_month || 0);
  const lastMonthRev = parseFloat(kpi.last_month?.revenue_lastmonth || 0);
  const trend = lastMonthRev > 0
    ? Math.round(((monthRev - lastMonthRev) / lastMonthRev) * 100)
    : null;

  const totalActiveOrders = (kpi.active_orders || []).reduce((s, x) => s + x.c, 0);
  const totalStockAlerts = (kpi.stock_alerts || []).reduce((s, x) => s + x.c, 0);

  return (
    <Box sx={{ p: 2 }}>
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#1565C0' }}>
            🎯 Yönetim Merkezi
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Son güncelleme: {lastUpdate.toLocaleTimeString('tr-TR')} · Otomatik refresh 30 sn
          </Typography>
        </Box>
        <Tooltip title="Şimdi yenile">
          <IconButton onClick={load} color="primary"><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {/* ANA KPI'LAR */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<AttachMoneyIcon />} color="#10b981"
            label="Bugün Ciro"
            value={formatTL(kpi.today?.revenue_today)}
            sub={`${kpi.today?.orders_today || 0} sipariş`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<TrendingUpIcon />} color="#1565C0"
            label="Bu Ay Ciro"
            value={formatTL(monthRev)}
            sub={`${kpi.this_month?.orders_month || 0} sipariş`}
            trend={trend}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<ShoppingCartIcon />} color="#E67E22"
            label="Aktif Sipariş"
            value={totalActiveOrders}
            sub="Hazırlanıyor / kargoda / bekliyor"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<WarningAmberIcon />} color="#ef4444"
            label="Stok Alert"
            value={totalStockAlerts}
            sub="Çözülmemiş uyarı"
          />
        </Grid>
      </Grid>

      {/* 7 GÜN CİRO GRAFİĞİ + BİLDİRİMLER */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={700}>📈 Son 7 Gün Ciro</Typography>
                <Typography variant="caption" color="text.secondary">
                  Toplam: {formatTL((kpi.revenue_7d || []).reduce((s, d) => s + parseFloat(d.revenue || 0), 0))}
                </Typography>
              </Stack>
              <MiniBarChart data={kpi.revenue_7d} height={140} />
              <Stack direction="row" justifyContent="space-between" mt={1.5}>
                {(kpi.revenue_7d || []).map((d, i) => (
                  <Typography key={i} variant="caption" sx={{ flex: 1, textAlign: 'center' }}>
                    {new Date(d.d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" fontWeight={700}>🔔 Bildirimler</Typography>
                <Chip label={notifications.length} color="error" size="small" />
              </Stack>
              <Box sx={{ maxHeight: 160, overflow: 'auto' }}>
                {notifications.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">Aktif bildirim yok ✅</Typography>
                ) : notifications.slice(0, 8).map((n, i) => (
                  <Box key={i} sx={{
                    p: 1, mb: 0.5, borderRadius: 1, cursor: n.link ? 'pointer' : 'default',
                    bgcolor: n.severity === 'error' ? '#fef2f2' :
                             n.severity === 'warning' ? '#fffbeb' :
                             n.severity === 'success' ? '#f0fdf4' : '#eff6ff',
                    '&:hover': n.link ? { bgcolor: '#f1f5f9' } : {}
                  }} onClick={() => n.link && navigate(n.link)}>
                    <Typography variant="body2" fontWeight={600}>{n.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{n.body}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SİPARİŞ STATÜ + STOK + NPS + EMAIL */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                Sipariş Statüleri
              </Typography>
              <Stack spacing={1} mt={1}>
                {(kpi.active_orders || []).map(s => (
                  <Stack key={s.status} direction="row" justifyContent="space-between" alignItems="center">
                    <Chip label={s.status} size="small" />
                    <Typography variant="body2" fontWeight={700}>{s.c}</Typography>
                  </Stack>
                ))}
                {totalActiveOrders === 0 && <Typography variant="body2" color="text.secondary">Aktif sipariş yok</Typography>}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                Stok Uyarıları
              </Typography>
              <Stack spacing={1} mt={1}>
                {(kpi.stock_alerts || []).map(s => (
                  <Stack key={s.alert_type} direction="row" justifyContent="space-between" alignItems="center">
                    <Chip label={s.alert_type} size="small"
                      color={s.alert_type === 'out_of_stock' ? 'error' : s.alert_type === 'critical' ? 'warning' : 'default'} />
                    <Typography variant="body2" fontWeight={700}>{s.c}</Typography>
                  </Stack>
                ))}
                {totalStockAlerts === 0 && <Typography variant="body2" color="success.main">✓ Stok stabil</Typography>}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <StarIcon color="warning" />
                <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                  NPS (90 gün)
                </Typography>
              </Stack>
              {kpi.nps?.total > 0 ? (
                <>
                  <Typography variant="h3" fontWeight={800}
                    color={kpi.nps.score >= 50 ? 'success.main' : kpi.nps.score >= 0 ? 'warning.main' : 'error.main'}>
                    {kpi.nps.score}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {kpi.nps.promoters} promoter · {kpi.nps.passives} pasif · {kpi.nps.detractors} detractor
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">Henüz NPS yanıtı yok</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <EmailIcon color="primary" />
                <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600}>
                  Email (24h)
                </Typography>
              </Stack>
              <Typography variant="h3" fontWeight={800} color="primary.main">
                {kpi.email_24h?.sent_24h || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Gönderildi · {kpi.email_24h?.failed_24h || 0} hata
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* TOP ÜRÜNLER + SEGMENTLER */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>🏆 Top 5 Ürün (30 gün)</Typography>
              {(kpi.top_products_30d || []).map((p, i) => (
                <Box key={p.id} sx={{ pb: 1, mb: 1, borderBottom: '1px solid #f1f5f9' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {i+1}. {p.name?.slice(0, 50)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.sku || `#${p.id}`} · {p.order_count} sipariş
                      </Typography>
                    </Box>
                    <Chip label={`${p.total_qty} adet`} color="primary" size="small" />
                  </Stack>
                </Box>
              ))}
              {(!kpi.top_products_30d || kpi.top_products_30d.length === 0) && (
                <Typography variant="body2" color="text.secondary">Veri yok</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <GroupsIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Müşteri Segmentleri</Typography>
              </Stack>
              {(kpi.segments || []).map(s => (
                <Box key={s.code} mb={1.5}>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                    <Typography variant="body2" color="primary.main" fontWeight={700}>{s.c}</Typography>
                  </Stack>
                  <LinearProgress variant="determinate"
                    value={Math.min(100, (s.c / Math.max(...kpi.segments.map(x => x.c), 1)) * 100)}
                    sx={{ height: 6, borderRadius: 3 }} />
                </Box>
              ))}
              {(!kpi.segments || kpi.segments.length === 0) && (
                <Typography variant="body2" color="text.secondary">Segment verisi yok</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* CART ABANDONED + UPCOMING DUE */}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} md={6}>
          <Alert severity="info" icon={<LocalShippingIcon />}>
            <strong>Vade yaklaşan:</strong> {kpi.upcoming_due?.c || 0} sipariş · toplam {formatTL(kpi.upcoming_due?.amount || 0)}
          </Alert>
        </Grid>
        <Grid item xs={12} md={6}>
          <Alert severity="warning" icon={<ShoppingCartIcon />}>
            <strong>Sepet terk (24h):</strong> {kpi.cart_abandoned_24h?.c || 0} kullanıcı sepet'te bıraktı
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
}
